import jsYaml from 'js-yaml';
import YAML, { isMap, type Document } from 'yaml';
import languagesMap from './languages.json';
import { finalizeYamlDocument } from './processYaml.js';
import {
  extractAudioAiJitConfig,
  getPatchValue,
  isAudioJitEnabledForLocale,
  type CourseAudioJitConfig
} from './audioJitCourseConfig';

export type LegacyAudioSource = 'course.audio' | 'patches';

export type StrippableLocale = {
  locale: string;
  source: LegacyAudioSource;
  path: string;
};

export type StripLegacyAudioPlan = {
  strippableLocales: StrippableLocale[];
  unchanged: boolean;
  updatedYaml: string;
};

const AUDIO_PATCH_PREFIX = '$course.audio.';

function getTier2AudioEntries(
  parsed: Record<string, unknown>
): StrippableLocale[] {
  const entries: StrippableLocale[] = [];

  const course = parsed.course as Record<string, unknown> | undefined;
  const audio = course?.audio as Record<string, string> | undefined;
  if (audio && typeof audio === 'object') {
    for (const [locale, path] of Object.entries(audio)) {
      if (languagesMap[locale] && typeof path === 'string' && path.trim()) {
        entries.push({ locale, source: 'course.audio', path });
      }
    }
  }

  const patches = parsed.patches as Record<string, unknown> | undefined;
  if (patches && typeof patches === 'object') {
    for (const [patchKey, patchEntry] of Object.entries(patches)) {
      if (!patchKey.startsWith(AUDIO_PATCH_PREFIX)) continue;
      const locale = patchKey.slice(AUDIO_PATCH_PREFIX.length);
      if (!languagesMap[locale]) continue;
      const path = getPatchValue(patchEntry);
      if (typeof path === 'string' && path.trim()) {
        entries.push({ locale, source: 'patches', path });
      }
    }
  }

  return entries;
}

/** Remove Tier-2 audio keys in-place on a CST-preserving YAML document. */
export function stripLegacyAudioPathsInDocument(
  doc: Document.Parsed,
  strippableLocales: StrippableLocale[]
): void {
  const patchLocales = strippableLocales
    .filter(entry => entry.source === 'patches')
    .map(entry => entry.locale);
  const courseLocales = strippableLocales
    .filter(entry => entry.source === 'course.audio')
    .map(entry => entry.locale);

  if (patchLocales.length > 0) {
    const patches = doc.get('patches');
    if (patches && isMap(patches)) {
      for (const locale of patchLocales) {
        patches.delete(`${AUDIO_PATCH_PREFIX}${locale}`);
      }
      if (patches.items.length === 0) {
        doc.delete('patches');
      }
    }
  }

  if (courseLocales.length > 0) {
    const course = doc.get('course');
    if (course && isMap(course)) {
      const audio = course.get('audio');
      if (audio && isMap(audio)) {
        for (const locale of courseLocales) {
          audio.delete(locale);
        }
        if (audio.items.length === 0) {
          course.delete('audio');
        }
      }
      if (course.items.length === 0) {
        doc.delete('course');
      }
    }
  }
}

export function getTier2LocalesFromYaml(yaml: string): string[] {
  try {
    const parsed = (jsYaml.load(yaml) as Record<string, unknown>) || {};
    return getTier2AudioEntries(parsed)
      .map(entry => entry.locale)
      .sort();
  } catch {
    return [];
  }
}

export function planStripLegacyAudioPaths(
  yaml: string,
  baseJitConfig: CourseAudioJitConfig | undefined
): StripLegacyAudioPlan {
  let parsed: Record<string, unknown>;
  try {
    parsed = (jsYaml.load(yaml) as Record<string, unknown>) || {};
  } catch {
    return { strippableLocales: [], unchanged: true, updatedYaml: yaml };
  }

  const tier2Entries = getTier2AudioEntries(parsed);
  const strippableLocales = tier2Entries.filter(entry =>
    isAudioJitEnabledForLocale(baseJitConfig, entry.locale)
  );

  if (strippableLocales.length === 0) {
    return { strippableLocales: [], unchanged: true, updatedYaml: yaml };
  }

  try {
    const doc = YAML.parseDocument(yaml);
    stripLegacyAudioPathsInDocument(doc, strippableLocales);
    return {
      strippableLocales,
      unchanged: false,
      updatedYaml: finalizeYamlDocument(doc)
    };
  } catch {
    return { strippableLocales: [], unchanged: true, updatedYaml: yaml };
  }
}

/**
 * A "full" customisation embeds the entire course (top-level `pages`), as opposed
 * to a lightweight patch customisation that only carries overrides (`patches` /
 * `course`). Full customisations are riskier to bulk-edit, so callers may want to
 * exclude them by default.
 */
export function isFullCustomisationYaml(yaml: string): boolean {
  try {
    const parsed = jsYaml.load(yaml) as Record<string, unknown> | undefined;
    return Array.isArray(parsed?.pages) && parsed.pages.length > 0;
  } catch {
    return false;
  }
}

/** Locales where the customisation YAML defines JiT overrides. */
export function getCustomJitOverrideLocales(yaml: string): string[] {
  try {
    const parsed = jsYaml.load(yaml) as Record<string, unknown>;
    const config = extractAudioAiJitConfig(parsed);
    if (!config) return [];

    if (config === true) {
      return ['*'];
    }

    if (
      typeof config === 'object' &&
      !Array.isArray(config) &&
      ('provider' in config ||
        'model' in config ||
        'voice' in config ||
        'locale' in config)
    ) {
      return ['*'];
    }

    if (typeof config === 'object' && !Array.isArray(config)) {
      return Object.keys(config)
        .filter(locale => languagesMap[locale])
        .sort();
    }

    return [];
  } catch {
    return [];
  }
}
